import { Permission } from '_metronic/helpers'
import { useAuth } from '../../../../auth'

/**
 * Grupos que recebem a permissão `Esus:config` no seed do backend
 * (`lauduzone-backend/src/main/resources/permission.json`). Essa permissão protege TODOS os
 * endpoints de `EsusConfiguracaoController` — leitura, gravação, substituição de credencial,
 * checklist e ativação/desativação — via
 * `@PreAuthorize("hasPermission(#empresaId, 'Empresa', 'Esus:config')")`.
 *
 * LIMITAÇÃO CONHECIDA (arquitetura): o frontend não consegue checar `Esus:config` pelo nome.
 * O endpoint `/usuarios/me` devolve `UsuarioDtoVinculo`, cujo `grupo` é montado por
 * `new GrupoDto(usuario.getGrupo())` — construtor que NÃO preenche a lista `perfis` (as
 * permissões). O access token também não carrega claim de autoridades consumível aqui. Ou seja,
 * a única informação de autorização disponível no cliente é o NOME DO GRUPO
 * (`currentUser.grupo.nome`), que é exatamente o que o resto do app já usa para gatear UI
 * (ver `Permission` em `_metronic/helpers/enums.ts`).
 *
 * Consequência: se um gestor de acesso alterar em runtime quais grupos possuem `Esus:config`
 * (endpoints `Perfil:add`/`Perfil:remove`), esta lista fica dessincronizada e a UI pode esconder
 * a ação de quem passou a ter permissão (ou exibi-la para quem a perdeu). O backend continua
 * sendo a autoridade real: em qualquer divergência a chamada volta 403 e o usuário recebe a
 * mensagem de `MSG_SEM_PERMISSAO_ESUS`. Para eliminar a limitação é preciso o backend expor as
 * permissões efetivas do usuário autenticado (ex.: `/usuarios/me` com `grupo.perfis`).
 */
export const GRUPOS_ESUS_CONFIG: Permission[] = [Permission.SuperAdmin, Permission.Admin]

export const MSG_SEM_PERMISSAO_ESUS =
  'Você não tem permissão para alterar a configuração da integração e-SUS APS. ' +
  'Solicite ao administrador da instituição a permissão "Esus:config".'

/**
 * Indica se o usuário autenticado pode executar as ações de escrita da configuração e-SUS
 * (salvar configuração, substituir credencial e ativar/desativar a integração).
 */
export const usePermissaoEsusConfig = (): boolean => {
  const { currentUser } = useAuth()
  return GRUPOS_ESUS_CONFIG.includes(currentUser?.grupo?.nome as Permission)
}

/** Rede de segurança: identifica a negativa de autorização devolvida pelo backend. */
export const isPermissaoNegadaEsus = (err: any): boolean => err?.response?.status === 403
